/* The Eight Days of Christmas · campaign clock and pure state helpers.
   No DOM in here: everything is a pure function so it can run under
   `node --test` (tests/ojx-clock.test.mjs) as well as in the browser,
   where it attaches to window.OJXClock. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.OJXClock = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var DAY_MS = 86400000;
  var DEFAULTS = { timeZone: 'Europe/London', rolloverHour: 8, totalDays: 8 };
  var STORAGE = { preview: 'ojx:preview', previewDay: 'ojx:previewDay' };

  /* ---------- clock ---------- */

  /* Wall-clock parts of an instant in a named time zone. Intl does the DST
     work; late November is GMT, but the engine stays correct year round. */
  function zonedParts(date, timeZone) {
    var fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
    var parts = {};
    fmt.formatToParts(date).forEach(function (p) {
      if (p.type !== 'literal') parts[p.type] = parseInt(p.value, 10);
    });
    if (parts.hour === 24) parts.hour = 0;
    return parts;
  }

  function parseISODate(value) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());
    return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
  }

  function dayNumber(y, m, d) { return Math.round(Date.UTC(y, m - 1, d) / DAY_MS); }

  /* {phase:'pre'|'live'|'post', index:1..total|null} from the live clock.
     A campaign day runs from rolloverHour on its date until rolloverHour the
     next morning; before that hour the previous day is still "today". */
  function resolveLive(opts) {
    opts = opts || {};
    var now = opts.now || new Date();
    var tz = opts.timeZone || DEFAULTS.timeZone;
    var rollover = typeof opts.rolloverHour === 'number' ? opts.rolloverHour : DEFAULTS.rolloverHour;
    var total = opts.totalDays || DEFAULTS.totalDays;
    var start = parseISODate(opts.startDate);
    if (!start) return { phase: 'pre', index: null };
    var p = zonedParts(now, tz);
    var effective = dayNumber(p.year, p.month, p.day);
    if (p.hour < rollover) effective -= 1;
    var offset = effective - dayNumber(start.y, start.m, start.d);
    if (offset < 0) return { phase: 'pre', index: null };
    if (offset >= total) return { phase: 'post', index: null };
    return { phase: 'live', index: offset + 1 };
  }

  /* Calendar data for day N (1-based): weekday names and formatted dates,
     derived from the start date so the copy never drifts from the clock. */
  function dayCalendar(startDate, n) {
    var start = parseISODate(startDate);
    if (!start) return null;
    var d = new Date(Date.UTC(start.y, start.m - 1, start.d + (n - 1)));
    var long = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    var dowLong = long[d.getUTCDay()];
    var month = months[d.getUTCMonth()];
    return {
      dow: dowLong.slice(0, 3),
      dowLong: dowLong,
      date: d.getUTCDate() + ' ' + month,
      dateShort: dowLong.slice(0, 3) + ' ' + d.getUTCDate() + ' ' + month.slice(0, 3),
      iso: d.toISOString().slice(0, 10)
    };
  }

  /* ---------- preview override ---------- */

  /* '1'..'8' → that day · 'pre'|'0' → pre-launch · 'post'|'9' → post-campaign
     · 'live' → clear the override. Anything else is ignored. */
  function parsePreviewToken(value, total) {
    total = total || DEFAULTS.totalDays;
    var v = String(value == null ? '' : value).trim().toLowerCase();
    if (v === '') return null;
    if (v === 'live') return { kind: 'live' };
    if (v === 'pre' || v === '0') return { kind: 'pre' };
    if (v === 'post' || v === String(total + 1)) return { kind: 'post' };
    if (/^\d+$/.test(v)) {
      var n = parseInt(v, 10);
      if (n >= 1 && n <= total) return { kind: 'day', index: n };
    }
    return null;
  }

  function tokenToResult(token) {
    if (token.kind === 'pre') return { phase: 'pre', index: null };
    if (token.kind === 'post') return { phase: 'post', index: null };
    return { phase: 'live', index: token.index };
  }

  function param(search, name) {
    var q = String(search || '');
    if (q.charAt(0) === '?') q = q.slice(1);
    var out = null;
    q.split('&').forEach(function (pair) {
      if (!pair) return;
      var i = pair.indexOf('=');
      var k = decodeURIComponent(i < 0 ? pair : pair.slice(0, i)).replace(/\+/g, ' ');
      if (k !== name) return;
      out = i < 0 ? '' : decodeURIComponent(pair.slice(i + 1).replace(/\+/g, ' '));
    });
    return out;
  }

  function safeGet(storage, key) { try { return storage ? storage.getItem(key) : null; } catch (e) { return null; } }
  function safeSet(storage, key, value) { try { if (storage) storage.setItem(key, value); } catch (e) {} }
  function safeRemove(storage, key) { try { if (storage) storage.removeItem(key); } catch (e) {} }

  /* Which day the page should show.
     Precedence: ?day=N → sessionStorage → live clock. Overrides only apply
     while preview is enabled (the section flag, ?preview=1 once per session)
     so customers can never open a future door by editing the URL. */
  function resolveToday(opts) {
    opts = opts || {};
    var total = opts.totalDays || DEFAULTS.totalDays;
    var storage = opts.storage || null;
    var search = opts.search || '';

    var previewQuery = param(search, 'preview');
    if (previewQuery === '1' || previewQuery === 'true') safeSet(storage, STORAGE.preview, '1');
    if (previewQuery === '0' || previewQuery === 'false') {
      safeRemove(storage, STORAGE.preview);
      safeRemove(storage, STORAGE.previewDay);
    }
    var previewEnabled = !!opts.previewFlag || safeGet(storage, STORAGE.preview) === '1';

    var live = resolveLive(opts);
    var result = { phase: live.phase, index: live.index, source: 'live', preview: previewEnabled, token: null };
    if (!previewEnabled) return result;

    var queryToken = parsePreviewToken(param(search, 'day'), total);
    if (queryToken) {
      if (queryToken.kind === 'live') { safeRemove(storage, STORAGE.previewDay); return result; }
      safeSet(storage, STORAGE.previewDay, tokenString(queryToken));
      return merge(result, tokenToResult(queryToken), 'query', queryToken);
    }
    var storedToken = parsePreviewToken(safeGet(storage, STORAGE.previewDay), total);
    if (storedToken && storedToken.kind !== 'live') {
      return merge(result, tokenToResult(storedToken), 'session', storedToken);
    }
    return result;
  }

  function tokenString(token) {
    if (token.kind === 'day') return String(token.index);
    return token.kind;
  }

  function merge(base, r, source, token) {
    base.phase = r.phase; base.index = r.index; base.source = source; base.token = tokenString(token);
    return base;
  }

  /* Staff switcher: persist a token for the rest of the session. */
  function setPreviewDay(storage, tokenValue, total) {
    var token = parsePreviewToken(tokenValue, total);
    if (!token || token.kind === 'live') { safeRemove(storage, STORAGE.previewDay); return null; }
    safeSet(storage, STORAGE.previewDay, tokenString(token));
    return tokenString(token);
  }

  /* ---------- per-visit state ---------- */

  function initialState() {
    return {
      layout: 'grid', todayOpen: false, doorOpening: false, doorOpened: false,
      modalIndex: null, copied: '', notified: [], picks: [], duo: 'zafran',
      rsvp: false, ballot: false, subscribed: false, previewDay: null
    };
  }

  /* Switching the previewed day resets everything about that day but keeps
     what belongs to the visitor: layout choice, newsletter signup, notify list. */
  function resetForDaySwitch(state) {
    var fresh = initialState();
    fresh.layout = state.layout;
    fresh.subscribed = state.subscribed;
    fresh.notified = state.notified.slice();
    fresh.previewDay = state.previewDay;
    return fresh;
  }

  /* Bundle chooser: toggle a chip, never more than `max` picks (a fourth
     click is ignored, as in the prototype). */
  function togglePick(picks, i, max) {
    max = max || 3;
    var next = picks.slice();
    var k = next.indexOf(i);
    if (k >= 0) next.splice(k, 1);
    else if (next.length < max) next.push(i);
    return next;
  }

  function bundleHint(count, max) {
    max = max || 3;
    var left = max - count;
    if (left <= 0) return 'Your third is with our compliments: three for £72.';
    return 'Choose ' + (left === 1 ? 'one more' : left + ' more') + '. £36 each, three for £72.';
  }

  /* 'past' | 'today' | 'future' for door n given the resolved day. */
  function dayStatus(n, today) {
    if (today.phase === 'pre') return 'future';
    if (today.phase === 'post') return 'past';
    if (n < today.index) return 'past';
    if (n > today.index) return 'future';
    return 'today';
  }

  function progressCount(today, doorOpened, total) {
    total = total || DEFAULTS.totalDays;
    if (today.phase === 'pre') return 0;
    if (today.phase === 'post') return total;
    return (today.index - 1) + (doorOpened ? 1 : 0);
  }

  return {
    DEFAULTS: DEFAULTS, STORAGE: STORAGE,
    zonedParts: zonedParts, parseISODate: parseISODate, resolveLive: resolveLive, dayCalendar: dayCalendar,
    parsePreviewToken: parsePreviewToken, resolveToday: resolveToday, setPreviewDay: setPreviewDay, param: param,
    initialState: initialState, resetForDaySwitch: resetForDaySwitch, togglePick: togglePick, bundleHint: bundleHint,
    dayStatus: dayStatus, progressCount: progressCount
  };
});
